// Two date libraries on purpose: the dashboard helpers below were written on
// moment and their callers pass moment ranges; the ledger helpers (from
// `toLedgerZone` down) take dayjs, which is what antd 5's pickers and the
// ledger table hand around. Neither set converts the other's values.
import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {
    LEDGER_API_DATE_FORMAT,
    LEDGER_DATE_FORMAT,
    LEDGER_TABLE_PLACEHOLDER,
    LEDGER_TIMESTAMP_TITLE_FORMAT,
    LEDGER_TIMEZONE,
    LEDGER_TIMEZONE_LABEL,
    LEDGER_TIME_FORMAT,
    resolveTimezoneLabel,
} from '../constants/ledger.constants';

// `tz` needs `utc`, and the `z` token (EDT / EST) needs `advancedFormat`.
// Extending is idempotent and global, matching how user.controller.js loads its
// own dayjs plugins at module scope.
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

/**
 * @param {string} timeFrame
 * @param {import('dayjs').Dayjs[] | null} [dateRange] dayjs range when timeFrame === 'custom'
 */
export function calculateDateRange(timeFrame, dateRange) {
    const endDate = moment();

    switch (timeFrame) {
        case 'all':
            return {}; // No date filtering
        case '7d':
            return {
                start: endDate.clone().subtract(7, 'days').format('YYYY-MM-DD'),
                end: endDate.format('YYYY-MM-DD'),
            };
        case '30d':
            return {
                start: endDate.clone().subtract(30, 'days').format('YYYY-MM-DD'),
                end: endDate.format('YYYY-MM-DD'),
            };
        case 'mtd':
        case '1m':
            return {
                start: endDate.clone().startOf('month').format('YYYY-MM-DD'),
                end: endDate.format('YYYY-MM-DD'),
            };
        case '3m':
            return {
                start: endDate.clone().subtract(2, 'months').startOf('month').format('YYYY-MM-DD'),
                end: endDate.format('YYYY-MM-DD'),
            };
        case '6m':
            return {
                start: endDate.clone().subtract(5, 'months').startOf('month').format('YYYY-MM-DD'),
                end: endDate.format('YYYY-MM-DD'),
            };
        case 'ytd':
            return {
                start: endDate.clone().startOf('year').format('YYYY-MM-DD'),
                end: endDate.format('YYYY-MM-DD'),
            };
        case 'custom':
            if (!dateRange?.[0] || !dateRange?.[1]) {
                return { start: null, end: null };
            }
            return {
                start: dateRange[0].format('YYYY-MM-DD'),
                end: dateRange[1].format('YYYY-MM-DD'),
            };
        default:
            return {
                start: null,
                end: null,
            };
    }
}

/**
 * Human-readable inclusive date range for dashboard period tooltips (matches calculateDateRange / API).
 * @param {string} timeFrame
 * @param {import('dayjs').Dayjs[] | null} [dateRange]
 * @returns {{ line1: string, line2: string | null }} line2 is null when range not fixed yet (e.g. custom with no selection)
 */
export function getDashboardPeriodTooltipLines(timeFrame, dateRange) {
    const titles = {
        mtd: 'Month to date',
        '1m': 'Month to date',
        '3m': '3 months to date',
        '6m': '6 months to date',
        ytd: 'Year to date',
        custom: 'Custom range',
    };
    const line1 = titles[timeFrame] || timeFrame;

    if (timeFrame === 'custom' && (!dateRange?.[0] || !dateRange?.[1])) {
        return { line1, line2: null };
    }

    const { start, end } = calculateDateRange(timeFrame, dateRange);
    if (!start || !end) {
        return { line1, line2: null };
    }

    const from = moment(start).format('MMM D, YYYY');
    const to = moment(end).format('MMM D, YYYY');
    return { line1, line2: `${from} → ${to}` };
}

/** Sent as `grouping` on `/api/report`; charts are daily buckets server-side. */
export const DASHBOARD_REPORT_GROUPING = 'daily';

/**
 * Prior comparison window for dashboard tooltips (“Compared to …”).
 * Must stay aligned with `resolvePriorPeriodForKpi` in clockout-service `lib/kpiPriorPeriod.js` (same `timeFrame` + dates).
 * YTD → full prior calendar year; MTD-style (month start → same month) → full prior month; 3MTD/6MTD → three/six full months before current start; else same-length sliding window ending the day before current start.
 * @param {string} timeFrame
 * @param {import('dayjs').Dayjs[] | null} [dateRange]
 * @returns {{ start: moment.Moment, end: moment.Moment } | null}
 */
export function getDashboardPriorPeriodBounds(timeFrame, dateRange) {
    const { start, end } = calculateDateRange(timeFrame, dateRange);

    if (!start || !end) {
        const endDate = moment().subtract(30, 'days').endOf('day');
        const startDate = moment().subtract(60, 'days').startOf('day');
        return { start: startDate, end: endDate };
    }

    const startM = moment(start, 'YYYY-MM-DD');
    const endM = moment(end, 'YYYY-MM-DD');

    if (timeFrame === 'ytd') {
        const priorYearAnchor = startM.clone().startOf('year');
        return {
            start: priorYearAnchor.clone().subtract(1, 'year').startOf('year').startOf('day'),
            end: priorYearAnchor.clone().subtract(1, 'year').endOf('year').endOf('day'),
        };
    }

    // Align with clockout-service lib/kpiPriorPeriod.js
    // MTD-style periods: start on the 1st of the month, end within same month
    if ((timeFrame === 'mtd' || timeFrame === '1m') || (startM.isSame(endM, 'month') && startM.date() === 1)) {
        const prevMonth = startM.clone().subtract(1, 'month');
        return {
            start: prevMonth.clone().startOf('month').startOf('day'),
            end: prevMonth.clone().endOf('month').endOf('day'),
        };
    }

    const expected3mStart = endM.clone().subtract(2, 'months').startOf('month');
    if (startM.isSame(expected3mStart, 'day')) {
        const priorEnd = startM.clone().subtract(1, 'month').endOf('month').endOf('day');
        const priorStart = startM.clone().subtract(3, 'months').startOf('month').startOf('day');
        return { start: priorStart, end: priorEnd };
    }

    const expected6mStart = endM.clone().subtract(5, 'months').startOf('month');
    if (startM.isSame(expected6mStart, 'day')) {
        const priorEnd = startM.clone().subtract(1, 'month').endOf('month').endOf('day');
        const priorStart = startM.clone().subtract(6, 'months').startOf('month').startOf('day');
        return { start: priorStart, end: priorEnd };
    }

    const durationDays = endM.diff(startM, 'days');
    const previousEnd = startM.clone().subtract(1, 'day').endOf('day');
    const previousStart = previousEnd.clone().subtract(durationDays, 'days').startOf('day');
    return { start: previousStart, end: previousEnd };
}

/**
 * Hover text for “prior period” on KPI cards (matches the prior window the API uses when `timeFrame` is sent).
 */
export function getDashboardPriorPeriodRangeTooltip(timeFrame, dateRange) {
    if (timeFrame === 'custom') {
        return '';
    }
    const bounds = getDashboardPriorPeriodBounds(timeFrame, dateRange);
    if (!bounds) {
        return '';
    }
    const from = bounds.start.format('MMM D, YYYY');
    const to = bounds.end.format('MMM D, YYYY');
    return `Compared to ${from} → ${to}`;
}

/**
 * Format a `users.updated_at` value ("last time the user opened the experience")
 * as e.g. "March 2nd, 2026". Accepts epoch ms/seconds or a parseable date/ISO
 * string; returns "—" when empty and the raw value if it can't be parsed.
 */
export function formatLongDate(value) {
    if (value == null || value === '') {
        return '—';
    }
    const raw = String(value);
    const m = /^\d+$/.test(raw)
        ? moment(raw.length >= 13 ? Number(raw) : Number(raw) * 1000)
        : moment(value);
    return m.isValid() ? m.format('MMMM Do, YYYY') : raw;
}

// ---------------------------------------------------------------------------
// Consumer ledger timestamps. Pure functions, named exports: nothing here
// reads component state, so no view has to instantiate a controller — or mix
// fifteen unrelated cell renderers into itself — just to format a date. The
// shared table, the CSV export and the Reports filters all call these.
//
// Every formatter takes the zone as its LAST argument and defaults to
// `LEDGER_TIMEZONE`: the ledger endpoint declares the zone it evaluated its
// date range in (`timezone`), and the table renders in that zone so the
// headings, the cells and the filter presets can never be in two zones.
// ---------------------------------------------------------------------------

// `Intl.DateTimeFormat` is the only portable way to ask whether a zone exists,
// and building one is the expensive half. The answer never changes for a given
// string, so it is remembered: the table asks on every render.
const knownZones = new Map();

/**
 * Whether a string is an IANA zone this runtime knows.
 *
 * Worth checking rather than trusting: `dayjs().tz()` throws a `RangeError` on
 * an unknown zone, so a typo in a declared zone would take out every date cell
 * in the table rather than degrading to the fallback.
 *
 * @param {unknown} zone
 * @returns {boolean}
 */
export function isValidTimezone(zone) {
    if (!zone || typeof zone !== 'string') return false;
    if (!knownZones.has(zone)) {
        let known = false;
        try {
            new Intl.DateTimeFormat('en-US', { timeZone: zone });
            known = true;
        } catch (_error) {
            known = false;
        }
        knownZones.set(zone, known);
    }
    return knownZones.get(zone);
}

/**
 * The zone to render ledger rows in: the one the ledger endpoint declared for
 * this payload (`timezone`, clockout-service #316) when it is usable, else
 * `LEDGER_TIMEZONE`. The endpoint is the source of truth for which zone its
 * date range was evaluated in, so rendering in anything else would put the
 * filter presets and the cells in two different zones.
 *
 * @param {string|null|undefined} declared `timezone` from the ledger response
 * @returns {string} an IANA zone
 */
export function resolveLedgerRenderZone(declared) {
    return isValidTimezone(declared) ? declared : LEDGER_TIMEZONE;
}

// One label per zone for the app's lifetime. `resolveTimezoneLabel` answers a
// generic name (`ET`, not `EDT`), so the answer does not change with DST and
// can be remembered — see the note on that function.
const zoneLabels = new Map([[LEDGER_TIMEZONE, LEDGER_TIMEZONE_LABEL]]);

/**
 * The short zone marker for the Created / Processed column headings: `ET`,
 * `CT`, `GMT+9`. See `resolveTimezoneLabel` in `constants/ledger.constants`
 * for why the generic form is used.
 *
 * @param {string} zone an IANA zone
 * @returns {string}
 */
export function formatLedgerTimezoneLabel(zone) {
    if (!zoneLabels.has(zone)) zoneLabels.set(zone, resolveTimezoneLabel(zone));
    return zoneLabels.get(zone);
}

/**
 * A ledger timestamp moved into the zone it should be read in, or null when
 * there is nothing valid to show.
 *
 * The wire value is a UTC instant, so the conversion is exact — but note that
 * instant is only correct while the service process runs in the same timezone
 * its database writes `CURRENT_TIMESTAMP` in: the columns are TIMESTAMP WITHOUT
 * TIME ZONE and the service treats them as process-local wall clock
 * (clockout-service `core/sync_watchdog.js`, issue #251). Nothing here can
 * detect a violation of that.
 *
 * @param {string|Date|null} value
 * @param {string} [zone] an IANA zone; `LEDGER_TIMEZONE` when omitted
 * @returns {import('dayjs').Dayjs|null}
 */
export function toLedgerZone(value, zone = LEDGER_TIMEZONE) {
    if (!value) return null;
    const date = dayjs(value);
    return date.isValid() ? date.tz(zone) : null;
}

/**
 * The date half of a ledger timestamp, in `LEDGER_DATE_FORMAT` and in the
 * given zone — never the viewer's. That is what `toLedgerZone` is for, and it
 * is not cosmetic here: an instant near midnight falls on a different DATE for
 * two people reading the same row.
 *
 * The shape is named rather than spelled out: an earlier docstring claimed
 * `MMM D, YYYY` long after the constant had become `MM/DD/YYYY`.
 *
 * @param {string|Date|null} value
 * @param {string|null} [placeholder]
 * @param {string} [zone]
 * @returns {string|null}
 */
export function formatLedgerDate(value, placeholder = LEDGER_TABLE_PLACEHOLDER, zone = LEDGER_TIMEZONE) {
    return toLedgerZone(value, zone)?.format(LEDGER_DATE_FORMAT) ?? placeholder;
}

/**
 * The clock half of a ledger timestamp, in `LEDGER_TIME_FORMAT` and in the
 * given zone — fixed, never the viewer's. Two people in different zones have to
 * read the same row the same way, which is the whole point of `toLedgerZone`.
 *
 * 24-hour, for the reason that constant gives: with no AM/PM marker beside it,
 * `02:41:07` would be readable as either 2am or 2pm, and support reads these
 * columns to place a money movement in time.
 *
 * @param {string|Date|null} value
 * @param {string} [zone]
 * @returns {string|null} null when there is nothing to show
 */
export function formatLedgerTime(value, zone = LEDGER_TIMEZONE) {
    return toLedgerZone(value, zone)?.format(LEDGER_TIME_FORMAT) ?? null;
}

/**
 * The full timestamp with zone abbreviation and offset, for a cell `title` —
 * enough for a reader to convert it without knowing what EDT stands for.
 *
 * @param {string|Date|null} value
 * @param {string} [zone]
 * @returns {string|null}
 */
export function formatLedgerTimestampTitle(value, zone = LEDGER_TIMEZONE) {
    return toLedgerZone(value, zone)?.format(LEDGER_TIMESTAMP_TITLE_FORMAT) ?? null;
}

/**
 * `start` / `end` for the ledger endpoint's created-date filter, evaluated in
 * the zone the endpoint itself uses.
 *
 * Distinct from `calculateDateRange` above on purpose: that one takes "today"
 * from the viewer's clock (`moment()`), which is right for the dashboard but
 * wrong here — the service resolves `start`/`end` to midnight in its reporting
 * zone (`resolveCreatedAtWindow`, America/New_York), so "Last 7 days" chosen
 * at 22:00 Pacific would otherwise send tomorrow's date as `end`, and the
 * headings above the table would name a zone the range was not computed in.
 * A custom range is calendar dates the user picked and is passed through.
 *
 * @param {string} timeFrame one of `all` | `7d` | `30d` | `custom`
 * @param {import('dayjs').Dayjs[] | null} [dateRange] when `custom`
 * @param {string} [zone] the zone the endpoint declared; `LEDGER_TIMEZONE` when omitted
 * @returns {{ start: string|null, end: string|null }} both set or both null
 */
export function calculateLedgerDateRange(timeFrame, dateRange, zone = LEDGER_TIMEZONE) {
    const none = { start: null, end: null };
    if (timeFrame === 'custom') {
        if (!dateRange?.[0] || !dateRange?.[1]) return none;
        return {
            start: dateRange[0].format(LEDGER_API_DATE_FORMAT),
            end: dateRange[1].format(LEDGER_API_DATE_FORMAT),
        };
    }

    const days = { '7d': 7, '30d': 30 }[timeFrame];
    if (!days) return none;

    const today = dayjs().tz(zone);
    return {
        start: today.subtract(days, 'day').format(LEDGER_API_DATE_FORMAT),
        end: today.format(LEDGER_API_DATE_FORMAT),
    };
}
